/**
 * Der QueryClient der Anwendung — und die eine Regel, die nur hier stehen kann.
 *
 * Der Zugangsschutz (`<RequireSession>`) sieht nur die Sitzungsabfrage. Die aber wird 30 Sekunden
 * lang nicht neu gestellt, und eine Sitzung läuft nicht beim Laden ab, sondern mitten in der
 * Arbeit: der Nutzer kommt nach einer Stunde zurück, klickt auf „Provisionen“, und der Server
 * antwortet 401 `nicht_angemeldet`. Ohne diese Regel bleibt er auf einem Bildschirm mit einer
 * Fehlerkarte stehen, aus der kein Weg herausführt — nicht einmal zur Anmeldung, denn die
 * Kopfzeile hält die Sitzung für gültig.
 *
 * Also: sagt IRGENDEINE Abfrage oder Änderung „nicht angemeldet“, wird die Sitzungsabfrage
 * zurückgesetzt. Sie stellt sich neu, bekommt ihrerseits den 401, und der Zugangsschutz leitet zur
 * Anmeldung — mit dem Ziel im Suchparameter, damit der Nutzer nach dem Code wieder dort steht, wo
 * er war.
 *
 * Die Ausnahme ist keine Kleinigkeit: die Sitzungsabfrage selbst darf sich nicht zurücksetzen. Sie
 * täte es sonst endlos, denn ihr 401 löst genau diese Regel aus.
 */
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { istNichtAngemeldet } from './api'
import { SITZUNG_SCHLUESSEL } from './auth'

function istSitzungsSchluessel(schluessel: readonly unknown[]): boolean {
  return schluessel[0] === SITZUNG_SCHLUESSEL[0]
}

export function erstelleQueryClient(): QueryClient {
  let client: QueryClient | null = null

  const beiFehler = (fehler: unknown, eigenerSchluessel: readonly unknown[] | null): void => {
    if (!istNichtAngemeldet(fehler)) return
    if (eigenerSchluessel !== null && istSitzungsSchluessel(eigenerSchluessel)) return
    void client?.resetQueries({ queryKey: SITZUNG_SCHLUESSEL })
  }

  client = new QueryClient({
    queryCache: new QueryCache({
      onError: (fehler, abfrage) => {
        beiFehler(fehler, abfrage.queryKey)
      },
    }),
    mutationCache: new MutationCache({
      onError: (fehler) => {
        beiFehler(fehler, null)
      },
    }),
    defaultOptions: {
      queries: {
        // Ein 401 oder ein 422 wird nicht besser, wenn man ihn dreimal stellt.
        retry: false,
        refetchOnWindowFocus: true,
        staleTime: 30_000,
      },
    },
  })
  return client
}
